import "./AdminGrade.css";

import { Exam, Grade, Section } from "../../../../model/model";
import React, { useEffect, useMemo, useState } from "react";
import {
  getExamThunk,
  updateExamThunk,
} from "../../../../store/slices/adminThunks";
import { useAppDispatch, useAppSelector } from "../../../../store";

import Header from "../../../../components/Header";
import ReactDOM from "react-dom";
import { adminActions } from "../../../../store/slices/adminSlice";
import { examTransformer } from "./exam.transformer";
import { useNavigate } from "react-router-dom";

interface Props {
  examId: string | undefined;
}
export default function AdminGrade(props: Props) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const exam = useAppSelector((state) => state.admin.selectedExam);
  const eSections = useAppSelector((state) => state.admin.sections);
  // const examId = useParams().id;
  const { examId } = props;

  // Store local state for grades with validity
  const [grades, setGrades] = useState<Grade[]>([]);
  const [sections, setSections] = useState<Section[]>([]);

  useEffect(() => {
    // If exam is not loaded, fetch it using the ID from the URL
    if (examId) {
      dispatch(getExamThunk({ id: examId }));
    }
  }, [examId, dispatch]);

  useEffect(() => {
    if (exam && grades.length === 0) {
      setGrades(exam.grades);
    }
  }, [exam, grades]);

  // Process sections data whenever exam or sections change
  // and grades are empty
  // This ensures that sections are only processed once when the test is loaded
  useEffect(() => {
    if (exam && eSections) {
      if (sections.length === 0 && grades.length > 0) {
        const processedSections = processSections(eSections, grades);
        setSections(processedSections);
      }
    }
  }, [exam, sections, eSections, grades]);

  useEffect(() => {
    if (grades.length > 0 && eSections) {
      const processedSections = processSections(eSections, grades);
      setSections(processedSections);
    }
  }, [grades, eSections]);

  const onBackClick = () => {
    navigate(-1);
    dispatch(adminActions.setSelectedExam(null));
  };

  function processSections(sects: Section[], res: Grade[]) {
    return sects.map((section) => {
      const sectionResults = res.filter((r) => r.section === section.name);
      const score = sectionResults.reduce((acc, r) => {
        return acc + (r.autoPoints || 0) + (r.valid ? r.humanPoints || 0 : 0);
      }, 0);
      return {
        ...section,
        score,
      };
    });
  }

  const filters = useMemo(() => {
    return {
      auto: grades.filter((r) => r.autoPoints),
      human: grades.filter((r) => r.human),
      noAnswer: grades.filter((r) => !r.answer),
      noValid: grades.filter((r) => !r.valid),
      noAutoPoints: grades.filter((r) => !r.autoPoints),
      noHumanPoints: grades.filter((r) => !r.humanPoints),
      noHuman: grades.filter((r) => !r.human),
      noAnswerCount: grades.filter((r) => r.answer).length,
      noValidCount: grades.filter((r) => r.valid).length,
      noAutoPointsCount: grades.filter((r) => r.autoPoints).length,
      noHumanPointsCount: grades.filter((r) => r.humanPoints).length,
      noHumanCount: grades.filter((r) => r.human).length,
    };
  }, [grades]);

  console.log("AdminGrade", { filters });

  // Sort and filter human results
  const sorted = useMemo(() => {
    return grades
      ?.filter((r) => r.human)
      .filter((r) => !r.autoPoints)
      .filter((r) => r.answer)
      .sort((a, b) => {
        const parseId = (id: string) => {
          const parts = id.split("-").map((part) =>
            // handle cases like "test-9-a1" by removing the "a" and converting to number
            Number(part.includes("a") ? part.replace("a", "") : part)
          ); // Split by '-' and convert to numbers
          return parts.slice(1); // Returns an array of numbers, e.g., [1, 1]
        };

        const [aNum1, aNum2] = parseId(a.id);
        const [bNum1, bNum2] = parseId(b.id);

        // Compare the numeric parts
        return aNum1 - bNum1 || aNum2 - bNum2;
      });
  }, [grades]);

  // Calculate total points whenever results change
  const totalValidHumanPoints = useMemo(() => {
    return grades?.reduce((acc, r) => {
      return acc + (r.valid ? r.humanPoints || 0 : 0);
    }, 0);
  }, [grades]);

  const totalAutoPoints = useMemo(() => {
    return grades?.reduce((acc, r) => {
      return acc + (r.autoPoints || 0);
    }, 0);
  }, [grades]);

  const handleCheckboxChange = (resultId: string, checked: boolean) => {
    const updatedResults: Grade[] = grades.map((r) =>
      r.id === resultId
        ? { ...r, valid: checked, humanPoints: checked ? r.value : 0 }
        : r
    );

    setGrades(updatedResults);

    // Update Redux state
    if (exam) {
      dispatch(
        adminActions.setSelectedExam({
          ...exam,
          grades: updatedResults,
          humanPoints: updatedResults.reduce(
            (acc, r) => acc + (r.valid ? r.humanPoints || 0 : 0),
            0
          ),
        })
      );
    }
  };

  const handleValueChange = (resultId: string, value: number | undefined) => {
    console.log("handleValueChange", resultId, value);
    // TODO check value isn't higher than question value
    const updatedResults: Grade[] = grades.map((r) =>
      r.id === resultId ? { ...r, humanPoints: value ?? 0 } : r
    );
    setGrades(updatedResults);
    // Update Redux state
    if (exam) {
      dispatch(
        adminActions.setSelectedExam({
          ...exam,
          grades: updatedResults,
          humanPoints: updatedResults.reduce(
            (acc, r) => acc + (r.valid ? r.humanPoints : 0),
            0
          ),
        })
      );
    }
  };

  const renderFooterContent = () => {
    return ReactDOM.createPortal(
      <>
        <button
          className="loa-button"
          onClick={() => {
            if (exam) {
              const partial: Partial<Exam> = {
                id: exam.id,
                grades,
                humanPoints: totalValidHumanPoints,
                autoPoints: totalAutoPoints,
                noAnswerCount: exam.noAnswerCount,
                totalPoints: totalValidHumanPoints + totalAutoPoints,
              };
              dispatch(updateExamThunk(partial));
            }
          }}
        >
          Save Grade
        </button>
      </>,
      document.getElementById("loa-admin-footer") || document.body
    );
  };

  // console.log("AdminGrade", { exam, grades, sorted });

  if (!exam) {
    return <div className="loa-page">Loading...</div>;
  }

  const sum = sections.reduce(
    (sum, section) => {
      return {
        sum: sum.sum + section.score,
        total: sum.total + section.points,
      };
    },
    { sum: 0, total: 0 }
  );

  return (
    <>
      <div className="test-results-page">
        <Header>
          <>
            <div className="ptr" onClick={onBackClick}>
              &lt; Back
            </div>
            <div>
              Grading for <strong>{exam?.email}</strong>
            </div>
            <div>
              <a
                href={`/exam/${exam?.id}?status=open`}
                target="_blank"
                rel="noreferrer"
              >
                {exam?.id}
              </a>
            </div>
          </>
        </Header>
        {exam ? (
          <div className="test-results">
            <div className="test-results-data">
              <div>
                <p>
                  Date:{" "}
                  {examTransformer
                    .toDate(exam.completedAt)
                    ?.toLocaleDateString()}
                </p>
                <p>Auto Points: {totalAutoPoints}</p>
                <p>Human Points: {totalValidHumanPoints}</p>
                <p>Status: {exam.status}</p>
              </div>
              <div className="test-sections">
                {sections?.map((section) => {
                  const progress = (section.score / section.points) * 100;
                  const limit = (section.limit / section.max) * 100;
                  // console.log(section.name, { section, progress, limit });
                  return (
                    <React.Fragment key={section.id}>
                      <div>{section.level}:</div>
                      <div className="section-score">
                        <div
                          className="section-progress"
                          style={{ right: 100 - progress + "%" }}
                        />
                        <div
                          className="section-limit"
                          style={{ left: limit + "%" }}
                        >&nbsp;{section.required}</div>
                        <div className="section-value">
                          {section.score}/{section.points}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
              <div className="section-total">
                <strong>
                  Total: {sum.sum}/{sum.total}
                </strong>
              </div>
            </div>
            <div className="test-results-grid">
              <div className="test-results-grid-header">
                <div>ID</div>
                <div>Correct</div>
                <div>Answer</div>
                <div>Valid</div>
                <div>Value</div>
                <div>Points</div>
                <div>Section</div>
              </div>
              {sorted?.map((grade, index) => (
                <div
                  className="test-results-grid-row"
                  key={grade.id}
                  aria-details={grade.id}
                >
                  <div
                    className="test-results-grid-id"
                    onClick={() => window.open(`/exam/${exam.id}#${grade.id}`)}
                  >
                    {grade.id}
                  </div>
                  <div>{grade.correct}</div>
                  <div>{grade.answer}</div>
                  <div>
                    <input
                      type="checkbox"
                      checked={grade.valid}
                      onChange={(evt) =>
                        handleCheckboxChange(grade.id, evt.target.checked)
                      }
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={
                        grade.humanPoints ? grade.humanPoints : grade.value
                      }
                      className="test-results-grid-input"
                      size={2}
                      onBlur={(evt) => {
                        const value = evt.target.value;
                        handleValueChange(
                          grade.id,
                          value ? parseInt(value) : undefined
                        );
                      }}
                      onChange={(evt) => {
                        const value = evt.target.value;
                        handleValueChange(
                          grade.id,
                          value ? parseInt(value) : undefined
                        );
                      }}
                    />
                  </div>
                  <div>
                    {grade.valid ? grade.humanPoints : 0} / {grade.value}
                  </div>
                  <div>{grade.section}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="results-loading">Loading...</div>
        )}
      </div>
      {renderFooterContent()}
    </>
  );
}
